/**
 * inboxSmartViews — Italian smart-view chip vocabulary for the
 * Conversazioni hub. Parallel of
 * :file:`features/workspace/documents/utils/smartViews.ts` for the
 * Documents hub — same shape (id, label, matcher, badge) so the
 * :class:`InboxSmartViewChips` and :class:`SmartSourceChips`
 * components can share a presentation contract.
 *
 * Vocabulary (June 2026)
 * ----------------------
 *   - ``tutti``        → no filter; baseline view
 *   - ``unread``       → provider-truth ``is_read === false``
 *   - ``needs_reply``  → unread AND (subject ``?`` / question pattern OR
 *                        body contains a verb-phrase like "potresti"
 *                        / "puoi" / "mi servirebbe" / "could you" /
 *                        "would you"). FE heuristic until the BE ships a
 *                        ``needs_reply`` classification.
 *   - ``attachments``  → ``has_attachments === true``
 *   - ``starred``      → ``is_starred === true`` (provider truth)
 *   - ``today``        → ``received_at`` is within the local calendar
 *                        day (timezone-aware)
 *
 * The view is purely a **filter** — it does NOT change the grouping
 * mode. Smart-view "tutti" + thread grouping = the default hub.
 *
 * Why FE heuristics for ``needs_reply``
 * -------------------------------------
 * The BE proactive layer can classify a message as "needs reply", but
 * the live ``/inbox/live`` endpoint doesn't carry that flag yet. We use
 * a conservative heuristic that errs on the side of inclusion (unread +
 * Italian/English question patterns OR direct address); when the BE
 * field lands, the matcher becomes a 1-line read of that flag and the
 * heuristic moves to a fallback branch.
 */

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

import type { InboxLiveItem } from "@ando/core";


export type InboxSmartView =
  | "tutti"
  | "unread"
  | "needs_reply"
  | "attachments"
  | "starred"
  | "today";


export const INBOX_SMART_VIEWS: ReadonlyArray<InboxSmartView> = [
  "tutti",
  "unread",
  "needs_reply",
  "attachments",
  "starred",
  "today",
];


export const INBOX_SMART_VIEW_LABELS: Readonly<
  Record<InboxSmartView, MessageDescriptor>
> = {
  tutti: msg`Tutti`,
  unread: msg`Da leggere`,
  needs_reply: msg`Richiede risposta`,
  attachments: msg`Con allegato`,
  starred: msg`Preferiti`,
  today: msg`Oggi`,
};


export const DEFAULT_INBOX_SMART_VIEW: InboxSmartView = "tutti";


export function parseInboxSmartView(
  raw: string | undefined | null,
): InboxSmartView {
  if (typeof raw !== "string") return DEFAULT_INBOX_SMART_VIEW;
  return (INBOX_SMART_VIEWS as readonly string[]).includes(raw)
    ? (raw as InboxSmartView)
    : DEFAULT_INBOX_SMART_VIEW;
}

export function encodeInboxSmartView(
  view: InboxSmartView,
): string | undefined {
  return view === DEFAULT_INBOX_SMART_VIEW ? undefined : view;
}


// ── Reply-detection heuristics ─────────────────────────────────────────
//
// Italian + English question / direct-address patterns. The matcher is
// case-insensitive AND tokenised (\b word boundaries) so "PUOI" inside
// PUOIAMO doesn't false-positive. Pattern set kept short to keep the
// per-row cost trivial (each match is ~O(snippet length) regex).
const REPLY_HINT_PATTERNS: ReadonlyArray<RegExp> = [
  /\?/, // any direct question mark
  /\b(?:potresti|puoi|potete|mi\s+servirebbe|mi\s+aiuti|riesci|fammi\s+sapere|conferma|confermi|attendo)\b/i,
  /\b(?:could\s+you|would\s+you|can\s+you|please\s+confirm|let\s+me\s+know|awaiting)\b/i,
];


function looksLikeNeedsReply(item: InboxLiveItem): boolean {
  if (item.is_read) return false; // already-handled threads don't need pinging
  const hay = `${item.subject ?? ""}\n${item.snippet ?? ""}`;
  if (hay.length === 0) return false;
  for (const re of REPLY_HINT_PATTERNS) {
    if (re.test(hay)) return true;
  }
  return false;
}


/** Start-of-day in the local timezone (00:00:00.000) — mirrors
 *  ``recencyGroup.ts::startOfLocalDay``. */
function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}


function isToday(item: InboxLiveItem, now: Date): boolean {
  if (!item.received_at) return false;
  const t = Date.parse(item.received_at);
  if (Number.isNaN(t)) return false;
  return t >= startOfLocalDay(now);
}


/**
 * Pure matcher: ``true`` iff the item satisfies the smart view's
 * filter. Exported so the route can compute per-view counts without
 * duplicating the rules.
 */
export function matchesInboxSmartView(
  item: InboxLiveItem,
  view: InboxSmartView,
  now: Date = new Date(),
): boolean {
  switch (view) {
    case "tutti":
      return true;
    case "unread":
      return !item.is_read;
    case "needs_reply":
      return looksLikeNeedsReply(item);
    case "attachments":
      return item.has_attachments === true;
    case "starred":
      return item.is_starred === true;
    case "today":
      return isToday(item, now);
    default:
      return false;
  }
}


/** Convenience helper — drives the chip-strip badge counts. */
export function filterByInboxSmartView(
  items: readonly InboxLiveItem[],
  view: InboxSmartView,
  now: Date = new Date(),
): readonly InboxLiveItem[] {
  if (view === "tutti") return items;
  return items.filter((i) => matchesInboxSmartView(i, view, now));
}
